import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Tooltip } from '@material-ui/core';
import { Timer, ChevronRight, Schedule } from '@material-ui/icons';
import clsx from 'clsx';
import { ITournament, MatchStage, MatchStatus, CupRoundStatus, TournamentStage } from '@pvplevelup/core/dist/interfaces/Tournament/ITournament';
import { IMatchView, IRoundView, IMatchSideView, ICupResultView } from '@pvplevelup/core/dist/queries/tournament/tournament';
import { countRounds, eliminationRoundName } from '@pvplevelup/core/dist/tournament/TournamentBrackets';
import { PveIcons } from 'utilities/pve_icons';
import { PAGE_KIT } from 'components/Page/PageKit';
import { Segmented } from '../Pve/run/PveRunList';
import { TOURNAMENT_ACCENT, dungeonLabel, formatMinutes, formatClock, cupTeamClock, formatShortTime, formatDateTime } from './tournamentFormat';
import { scheduleStageOf } from '@pvplevelup/core/dist/tournament/TournamentSchedule';
import { ShuffleBoard } from './ShuffleBoard';

const CARD_WIDTH = 230;

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  scroller: {
    overflowX: 'auto',
    paddingBottom: 6,
  },
  bracket: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: `${CARD_WIDTH}px`,
    gridColumnGap: 28,
    alignItems: 'stretch',
  },
  column: {
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
  },
  columnTitle: {
    marginBottom: 10,
    fontSize: '0.7rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: PAGE_KIT.muted,
    textAlign: 'center',
  },
  columnMatches: {
    display: 'grid',
    alignItems: 'center',
    gridRowGap: 12,
  },
  sectionTitle: {
    margin: theme.spacing(2.5, 0, 1.25),
    fontSize: '0.78rem',
    fontWeight: 900,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, 0.85)',
  },
  match: {
    position: 'relative',
    borderRadius: 10,
    overflow: 'hidden',
    background: 'rgba(255, 255, 255, 0.035)',
    border: '1px solid rgba(255, 255, 255, 0.09)',
  },
  matchClickable: {
    cursor: 'pointer',
    transition: 'border-color 0.12s, transform 0.12s',
    '&:hover': {
      borderColor: 'rgba(255, 179, 0, 0.55)',
      transform: 'translateY(-1px)',
    },
  },
  matchLive: {
    borderColor: 'rgba(239, 83, 80, 0.6)',
    boxShadow: '0 0 0 1px rgba(239, 83, 80, 0.25), 0 0 18px rgba(239, 83, 80, 0.15)',
  },
  matchFaded: {
    opacity: 0.5,
  },
  matchHead: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    padding: '3px 9px',
    fontSize: '0.64rem',
    fontWeight: 800,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: PAGE_KIT.faint,
    background: 'rgba(0, 0, 0, 0.25)',
  },
  side: {
    display: 'grid',
    gridTemplateColumns: '22px minmax(0, 1fr) 24px',
    gridGap: 8,
    alignItems: 'center',
    height: 32,
    padding: '0 9px',
    fontSize: '0.86rem',
    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
  },
  sideWinner: {
    background: 'linear-gradient(90deg, rgba(255, 179, 0, 0.16), rgba(255, 179, 0, 0.02))',
    boxShadow: `inset 3px 0 0 ${TOURNAMENT_ACCENT}`,
    fontWeight: 800,
  },
  sideLoser: {
    color: PAGE_KIT.faint,
  },
  seed: {
    width: 22,
    height: 18,
    borderRadius: 6,
    display: 'grid',
    placeItems: 'center',
    fontSize: '0.66rem',
    fontWeight: 900,
    color: 'rgba(255, 255, 255, 0.7)',
    background: 'rgba(255, 255, 255, 0.08)',
  },
  sideName: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  placeholder: {
    fontStyle: 'italic',
    color: PAGE_KIT.faint,
    fontSize: '0.78rem',
  },
  score: {
    textAlign: 'right',
    fontWeight: 900,
    fontVariantNumeric: 'tabular-nums',
  },
  // ---------------------------------------------------------------- round robin
  groups: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
    gridGap: theme.spacing(2),
  },
  group: {
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 0.025)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    overflow: 'hidden',
  },
  groupHead: {
    padding: '9px 12px',
    fontWeight: 900,
    borderBottom: PAGE_KIT.divider,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.86rem',
    '& th': {
      padding: '7px 10px',
      fontSize: '0.66rem',
      fontWeight: 800,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: PAGE_KIT.faint,
      textAlign: 'right',
    },
    '& th:nth-child(2)': {
      textAlign: 'left',
    },
    '& td': {
      padding: '7px 10px',
      borderTop: PAGE_KIT.divider,
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
    },
    '& td:nth-child(2)': {
      textAlign: 'left',
      fontWeight: 700,
    },
  },
  rank: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'inline-grid',
    placeItems: 'center',
    fontSize: '0.72rem',
    fontWeight: 900,
    background: 'rgba(255, 255, 255, 0.08)',
  },
  qualified: {
    color: '#1a1300',
    background: `linear-gradient(135deg, #ffe082, ${TOURNAMENT_ACCENT})`,
  },
  fixtures: {
    padding: '6px 12px 10px',
  },
  fixture: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 54px minmax(0, 1fr)',
    gridGap: 8,
    alignItems: 'center',
    padding: '5px 0',
    fontSize: '0.82rem',
  },
  fixtureScore: {
    textAlign: 'center',
    fontWeight: 900,
    fontVariantNumeric: 'tabular-nums',
    borderRadius: 6,
    padding: '2px 0',
    background: 'rgba(255, 255, 255, 0.06)',
  },
  // ---------------------------------------------------------------- cup
  cupRounds: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(170px, 1fr)',
    gridGap: 12,
    overflowX: 'auto',
    paddingBottom: 4,
  },
  cupRound: {
    position: 'relative',
    borderRadius: 12,
    overflow: 'hidden',
    minHeight: 104,
    padding: '10px 12px',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.12s, transform 0.12s, box-shadow 0.12s',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  },
  cupRoundSelected: {
    boxShadow: `0 0 0 2px ${TOURNAMENT_ACCENT}`,
  },
  cupRoundClock: {
    marginTop: 4,
    fontSize: '0.72rem',
    fontWeight: 800,
    color: '#ef9a9a',
  },
  roundHead: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'baseline',
    gridColumnGap: 12,
  },
  roundNote: {
    fontSize: '0.78rem',
    color: PAGE_KIT.muted,
  },
  resultRow: {
    cursor: 'pointer',
    '&:hover td': {
      background: 'rgba(255, 255, 255, 0.04)',
    },
  },
  resultTime: {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 700,
  },
  goIcon: {
    fontSize: 18,
    verticalAlign: -4,
    color: PAGE_KIT.faint,
  },
  cupRoundIndex: {
    fontSize: '0.66rem',
    fontWeight: 900,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: TOURNAMENT_ACCENT,
  },
  cupRoundName: {
    marginTop: 3,
    fontWeight: 900,
    lineHeight: 1.2,
  },
  cupRoundMeta: {
    marginTop: 6,
    display: 'grid',
    gridAutoFlow: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '0.74rem',
    color: 'rgba(255, 255, 255, 0.75)',
  },
  pill: {
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: 9,
    fontSize: '0.64rem',
    fontWeight: 900,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  placeChip: {
    display: 'inline-grid',
    placeItems: 'center',
    minWidth: 26,
    height: 20,
    borderRadius: 6,
    fontSize: '0.72rem',
    fontWeight: 900,
    background: 'rgba(255, 255, 255, 0.07)',
  },
  points: {
    fontWeight: 900,
    color: TOURNAMENT_ACCENT,
  },
  empty: {
    color: PAGE_KIT.faint,
    fontSize: '0.88rem',
  },
  when: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridColumnGap: 3,
    alignItems: 'center',
    color: TOURNAMENT_ACCENT,
    textTransform: 'none',
    letterSpacing: 0,
  },
  columnTime: {
    marginTop: 2,
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: 0,
    textTransform: 'none',
    color: TOURNAMENT_ACCENT,
  },
  roundButton: {
    marginLeft: 2,
    padding: 2,
    color: PAGE_KIT.faint,
    '&:hover': {
      color: TOURNAMENT_ACCENT,
    },
  },
}));

/** a round organizers can give a start time (from a column title) */
export interface RoundTimeTarget {
  stage: string;
  round: number;
  total: number;
  label: string;
  at?: string | null;
}

/** set by the page: organizers can set a round's start time */
const RoundClick = React.createContext<((target: RoundTimeTarget) => void) | null>(null);

/** a match waiting for its scheduled time (D28) */
const waitingFor = (m: IMatchView) => !m.readySince && !!m.startsAt && (m.status == MatchStatus.Pending || m.status == MatchStatus.Ready) ? m.startsAt : null;

/** a column's title, with its round's time and (for organizers) a button to set it */
const RoundTitle: React.FC<{label: string, stage: string, round: number, total: number, matches: IMatchView[]}> = ({label, stage, round, total, matches}) => {
  const classes = useStyles();
  const onRound = React.useContext(RoundClick);
  const at = matches.find((m) => m.roundScheduledAt)?.roundScheduledAt || null;
  const scheduleStage = scheduleStageOf(stage);
  const open = matches.some((m) => !m.readySince && m.status != MatchStatus.Completed && m.status != MatchStatus.Bye && m.status != MatchStatus.Void);
  return (
    <div className={classes.columnTitle}>
      {label}
      {onRound && scheduleStage && open &&
        <Tooltip title={at ? `Starts ${formatDateTime(at)} · change` : "Set the round's start time"}>
          <IconButton className={classes.roundButton} size="small" onClick={() => onRound({stage: scheduleStage, round, total, label, at})}><Schedule style={{fontSize: 14}} /></IconButton>
        </Tooltip>}
      {at && <div className={classes.columnTime}>{formatShortTime(at)}</div>}
    </div>
  );
}

const statusLabel = (m: IMatchView) => m.status == MatchStatus.Live ? 'Live' : m.status == MatchStatus.Completed ? 'Final'
  : m.status == MatchStatus.Bye ? 'Bye' : m.status == MatchStatus.Ready ? 'Up next' : m.status == MatchStatus.Void ? 'Not needed' : '';

/** set by the page: organizers can open a match to decide it by hand */
const MatchClick = React.createContext<((m: IMatchView) => void) | null>(null);

const MatchCard: React.FC<{match: IMatchView}> = ({match: m}) => {
  const classes = useStyles();
  const onClick = React.useContext(MatchClick);
  // any match opens its detail (a "not needed" one has nothing to show)
  const clickable = !!onClick && m.status != MatchStatus.Void;
  const waiting = waitingFor(m);
  const faded = m.status == MatchStatus.Void || m.status == MatchStatus.Bye;
  const row = (side: IMatchSideView, key: 'a' | 'b') => {
    const won = m.winner == key;
    const lost = m.winner != null && !won;
    return (
      <div className={clsx(classes.side, {[classes.sideWinner]: won, [classes.sideLoser]: lost})}>
        <span className={classes.seed}>{side.seed || ''}</span>
        {side.name
          ? <Tooltip title={(side.members || []).map((u) => u.displayname).join(', ') || side.name}><span className={classes.sideName}>{side.name}</span></Tooltip>
          : <span className={clsx(classes.sideName, classes.placeholder)}>{side.bye ? 'Bye' : side.fromLabel || 'To be decided'}</span>}
        <span className={classes.score}>{m.status == MatchStatus.Completed || m.status == MatchStatus.Live ? side.score || 0 : ''}</span>
      </div>
    );
  };
  return (
    <div className={clsx(classes.match, {[classes.matchLive]: m.status == MatchStatus.Live, [classes.matchFaded]: faded, [classes.matchClickable]: clickable})}
      onClick={() => clickable && onClick && onClick(m)} title={clickable ? 'Open the match' : undefined}>
      <div className={classes.matchHead}>
        <span>Bo{m.bestOf}</span>
        {waiting
          ? <Tooltip title={`Starts ${formatDateTime(waiting)}${m.scheduledAt ? ' (its own time)' : ''}`}><span className={classes.when}><Schedule style={{fontSize: 11}} />{formatShortTime(waiting)}</span></Tooltip>
          : <span style={m.status == MatchStatus.Live ? {color: '#ef9a9a'} : undefined}>{statusLabel(m)}</span>}
      </div>
      {row(m.a, 'a')}
      {row(m.b, 'b')}
    </div>
  );
}

/** columns of rounds; later rounds spread out so each match sits between the two that feed it */
const Columns: React.FC<{matches: IMatchView[], title: (round: number, total: number) => string}> = ({matches, title}) => {
  const classes = useStyles();
  const rounds = Array.from(new Set(matches.map((m) => m.round))).sort((a, b) => a - b);
  const firstCount = Math.max(1, matches.filter((m) => m.round == rounds[0]).length);
  return (
    <div className={classes.scroller}>
      <div className={classes.bracket}>
        {rounds.map((round) => {
          const list = matches.filter((m) => m.round == round).sort((a, b) => a.slot - b.slot);
          return (
            <div key={round} className={classes.column}>
              <RoundTitle label={title(round, rounds[rounds.length - 1])} stage={list[0]?.stage} round={round} total={rounds[rounds.length - 1]} matches={list} />
              <div className={classes.columnMatches} style={{gridTemplateRows: `repeat(${list.length}, 1fr)`, minHeight: firstCount * 98}}>
                {list.map((m) => <MatchCard key={String(m._id)} match={m} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** win/loss table for a round-robin group: wins, then head-to-head, then game difference (D16) */
function standings(matches: IMatchView[]) {
  const rows: Record<string, {entry: string, name: string, played: number, wins: number, losses: number, gamesFor: number, gamesAgainst: number}> = {};
  const add = (side: IMatchSideView) => {
    const key = String(side.entry || '');
    if(key && !rows[key])
      rows[key] = {entry: key, name: side.name || 'Unknown', played: 0, wins: 0, losses: 0, gamesFor: 0, gamesAgainst: 0};
    return rows[key];
  };
  matches.forEach((m) => {
    const a = add(m.a), b = add(m.b);
    if(m.status != MatchStatus.Completed || !a || !b)
      return;
    a.played++; b.played++;
    a.gamesFor += m.a.score || 0; a.gamesAgainst += m.b.score || 0;
    b.gamesFor += m.b.score || 0; b.gamesAgainst += m.a.score || 0;
    if(m.winner == 'a'){ a.wins++; b.losses++; }
    else if(m.winner == 'b'){ b.wins++; a.losses++; }
  });
  const headToHead = (x: string, y: string) => {
    const m = matches.find((mm) => mm.status == MatchStatus.Completed && ((String(mm.a.entry) == x && String(mm.b.entry) == y) || (String(mm.a.entry) == y && String(mm.b.entry) == x)));
    if(!m || !m.winner) return 0;
    const winner = String(m[m.winner].entry);
    return winner == x ? -1 : 1;
  };
  return Object.values(rows).sort((x, y) => y.wins - x.wins || headToHead(x.entry, y.entry)
    || (y.gamesFor - y.gamesAgainst) - (x.gamesFor - x.gamesAgainst) || x.name.localeCompare(y.name));
}

const Groups: React.FC<{matches: IMatchView[], qualifiers?: number | null}> = ({matches, qualifiers}) => {
  const classes = useStyles();
  const onMatch = React.useContext(MatchClick);
  const groups = Array.from(new Set(matches.map((m) => m.group || 0))).sort((a, b) => a - b);
  return (
    <div className={classes.groups}>
      {groups.map((g) => {
        const list = matches.filter((m) => (m.group || 0) == g);
        const table = standings(list);
        const rounds = Array.from(new Set(list.map((m) => m.round))).sort((a, b) => a - b);
        return (
          <div key={g} className={classes.group}>
            <div className={classes.groupHead}>{groups.length > 1 ? `Group ${String.fromCharCode(65 + g)}` : 'Standings'}</div>
            <table className={classes.table}>
              <thead><tr><th style={{textAlign: 'left', width: 34}}>#</th><th>{'Team'}</th><th>P</th><th>W</th><th>L</th><th>+/-</th></tr></thead>
              <tbody>
                {table.map((row, i) => (
                  <tr key={row.entry}>
                    <td style={{textAlign: 'left'}}><span className={clsx(classes.rank, {[classes.qualified]: qualifiers != null && groups.length > 1 && i < qualifiers})}>{i + 1}</span></td>
                    <td>{row.name}</td><td>{row.played}</td><td>{row.wins}</td><td>{row.losses}</td>
                    <td>{row.gamesFor - row.gamesAgainst > 0 ? '+' : ''}{row.gamesFor - row.gamesAgainst}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={classes.fixtures}>
              {rounds.map((r) => (
                <div key={r}>
                  <div style={{textAlign: 'left', margin: '8px 0 2px'}}>
                    <RoundTitle label={`Round ${r}`} stage={MatchStage.Group} round={r} total={rounds[rounds.length - 1]} matches={matches.filter((m) => m.round == r)} />
                  </div>
                  {list.filter((m) => m.round == r).map((m) => (
                    <div key={String(m._id)} className={clsx(classes.fixture, {[classes.matchClickable]: !!onMatch && m.status != MatchStatus.Void})}
                      onClick={() => onMatch && m.status != MatchStatus.Void && onMatch(m)}>
                      <span className={classes.sideName} style={{textAlign: 'right', fontWeight: m.winner == 'a' ? 800 : 500}}>{m.a.name}</span>
                      <span className={classes.fixtureScore} style={m.status == MatchStatus.Live ? {color: '#ef9a9a'} : undefined}>
                        {m.status == MatchStatus.Completed || m.status == MatchStatus.Live ? `${m.a.score || 0} – ${m.b.score || 0}`
                          : waitingFor(m) ? <Tooltip title={`Starts ${formatDateTime(waitingFor(m))}`}><span className={classes.when}>{formatShortTime(waitingFor(m))}</span></Tooltip> : `Bo${m.bestOf}`}
                      </span>
                      <span className={classes.sideName} style={{fontWeight: m.winner == 'b' ? 800 : 500}}>{m.b.name}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const ROUND_STATUS: Record<string, {label: string, color: string}> = {
  [CupRoundStatus.Pending]: {label: 'Up next', color: '#90a4ae'},
  [CupRoundStatus.Running]: {label: 'Running', color: '#ef5350'},
  [CupRoundStatus.Completed]: {label: 'Done', color: '#66bb6a'},
};

/** how a team's run in a round went */
function resultState(round: IRoundView, res: ICupResultView): {label: string, color: string, note?: string} {
  if(!res.finished && round.status == CupRoundStatus.Running)
    return res.startedAt ? {label: 'Running', color: '#ef5350'} : {label: 'Not started', color: '#90a4ae', note: 'The team has to start its run (its first fight) before the start window closes, or it forfeits.'};
  if(!res.finished)
    return {label: 'Waiting', color: '#90a4ae'};
  if(!res.valid)
    return {label: 'Invalid', color: '#9e9e9e', note: res.invalidReason || 'The run is not valid, so it takes last place.'};
  if(res.completed)
    return {label: 'Cleared', color: '#66bb6a'};
  return {label: 'Partial', color: '#ffa726', note: 'Not cleared: placed by bosses killed, then time.'};
}

/** a ticking clock, for the running round's time limit */
function useClock(active: boolean) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if(!active)
      return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

/** one round: every team's run, best first */
const RoundResults: React.FC<{round: IRoundView, previous?: IRoundView, now: number}> = ({round, previous, now}) => {
  const classes = useStyles();
  const history = useHistory();
  if(round.status == CupRoundStatus.Pending)
    return <div className={classes.empty}>{previous ? `Starts as soon as ${dungeonLabel(previous.instance)} is over.` : 'Starts when the cup begins.'}</div>;
  const done = round.status == CupRoundStatus.Completed;
  const results = round.results.slice().sort((a, b) => done
    ? (a.place ?? 99) - (b.place ?? 99) || (a.name || '').localeCompare(b.name || '')
    // still running: finished runs first, quickest first
    : Number(!!b.finished) - Number(!!a.finished) || (a.clearTime ?? a.timeElapsed ?? 1e9) - (b.clearTime ?? b.timeElapsed ?? 1e9));
  return (
    <div className={classes.group}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th style={{textAlign: 'left', width: 34}}>#</th>
            <th>Team</th>
            <th>Result</th>
            <th>Bosses</th>
            <th>Time</th>
            <th>Points</th>
            <th style={{width: 24}} />
          </tr>
        </thead>
        <tbody>
          {results.map((res) => {
            const state = resultState(round, res);
            const time = res.completed ? res.clearTime : res.timeElapsed;
            const clock = round.status == CupRoundStatus.Running ? cupTeamClock(res, now) : null;
            const pill = <span className={classes.pill} style={{color: state.color, background: `${state.color}26`}}>{state.label}</span>;
            return (
              <tr key={res.team} className={res.pve ? classes.resultRow : undefined} onClick={res.pve ? () => history.push(`/pve/run/${res.pve}`) : undefined}>
                <td style={{textAlign: 'left'}}><span className={clsx(classes.rank, {[classes.qualified]: done && res.place == 1})}>{done ? res.place ?? '–' : '–'}</span></td>
                <td>{res.name}</td>
                <td>{state.note ? <Tooltip title={state.note} placement="top">{pill}</Tooltip> : pill}</td>
                <td>{res.finished ? res.killed ?? 0 : '–'}</td>
                <td className={classes.resultTime}>
                  {res.finished && time ? formatMinutes(time)
                    : clock ? <Tooltip title={clock.kind == 'start' ? 'Left to start the run' : 'Left of the time limit'}><span style={{color: clock.kind == 'start' ? '#90a4ae' : '#ef9a9a'}}>{formatClock(clock.ms)}</span></Tooltip>
                    : '–'}
                </td>
                <td className={classes.points}>{done ? res.points ?? 0 : '–'}</td>
                <td>{res.pve && <ChevronRight className={classes.goIcon} />}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const Cup: React.FC<{rounds: IRoundView[]}> = ({rounds}) => {
  const classes = useStyles();
  const running = rounds.find((r) => r.status == CupRoundStatus.Running);
  const lastDone = rounds.slice().reverse().find((r) => r.status == CupRoundStatus.Completed);
  const [picked, setPicked] = useState<number | null>(null);
  const now = useClock(!!running);
  const selected = rounds.find((r) => r.index == picked) || running || lastDone || rounds[0];

  const teams: Record<string, {team: string, name: string, points: number, places: Array<number | null>}> = {};
  rounds.forEach((r, i) => r.results.forEach((res) => {
    const row = teams[res.team] || (teams[res.team] = {team: res.team, name: res.name || 'Unknown team', points: 0, places: rounds.map(() => null)});
    row.points += res.points || 0;
    row.places[i] = r.status == CupRoundStatus.Completed ? (res.place ?? null) : null;
  }));
  const table = Object.values(teams).sort((a, b) => b.points - a.points || a.name.localeCompare(b.name));
  // ties share a place (D6)
  let place = 0;
  const places = table.map((row, i) => (i > 0 && row.points == table[i - 1].points) ? place : (place = i + 1));
  const finishedCount = selected ? selected.results.filter((r) => r.finished).length : 0;
  return (
    <div>
      <div className={classes.cupRounds}>
        {rounds.map((r) => {
          const status = ROUND_STATUS[r.status] || ROUND_STATUS.Pending;
          const image = PveIcons[r.instance];
          return (
            <div key={String(r._id)} className={clsx(classes.cupRound, {[classes.cupRoundSelected]: selected && r.index == selected.index})} onClick={() => setPicked(r.index)}
              style={{backgroundImage: `linear-gradient(180deg, rgba(14, 14, 18, 0.55), rgba(14, 14, 18, 0.95))${image ? `, url(${image})` : ''}`,
                borderColor: r.status == CupRoundStatus.Running ? 'rgba(239, 83, 80, 0.6)' : undefined}}>
              <div className={classes.cupRoundIndex}>Round {r.index}</div>
              <div className={classes.cupRoundName}>{dungeonLabel(r.instance)}</div>
              <div className={classes.cupRoundMeta}>
                <span><Timer style={{fontSize: 13, verticalAlign: -2, marginRight: 3}} />{r.timeLimitMinutes} min</span>
                <span className={classes.pill} style={{color: status.color, background: `${status.color}26`}}>{status.label}</span>
              </div>
              {r.status == CupRoundStatus.Pending && r.scheduledAt &&
                <Tooltip title={`Starts ${formatDateTime(r.scheduledAt)}`}><div className={classes.cupRoundClock} style={{color: TOURNAMENT_ACCENT}}>Starts {formatShortTime(r.scheduledAt)}</div></Tooltip>}
              {r.status == CupRoundStatus.Running && (() => {
                // while teams are still starting: the start window; then how many runs are going
                const unstarted = r.results.filter((x) => !x.finished && !x.startedAt && x.startBy);
                const going = r.results.filter((x) => !x.finished && x.startedAt).length;
                return unstarted.length > 0
                  ? <div className={classes.cupRoundClock}>Start within {formatClock(new Date(unstarted[0].startBy!).getTime() - now)}</div>
                  : going > 0 ? <div className={classes.cupRoundClock}>{going} {going == 1 ? 'run' : 'runs'} going</div> : null;
              })()}
            </div>
          );
        })}
      </div>
      {selected &&
        <React.Fragment>
          <div className={clsx(classes.sectionTitle, classes.roundHead)}>
            <span>Round {selected.index} · {dungeonLabel(selected.instance)}</span>
            {selected.status == CupRoundStatus.Running &&
              <span className={classes.roundNote} style={{textTransform: 'none', letterSpacing: 0, fontWeight: 600}}>{finishedCount} of {selected.results.length} teams done</span>
            }
          </div>
          <RoundResults round={selected} previous={rounds.find((r) => r.index == selected.index - 1)} now={now} />
        </React.Fragment>
      }
      <div className={classes.sectionTitle}>Standings</div>
      <div className={classes.group}>
        <table className={classes.table}>
          <thead><tr><th style={{textAlign: 'left', width: 34}}>#</th><th>Team</th>{rounds.map((r) => <th key={r.index}>R{r.index}</th>)}<th>Points</th></tr></thead>
          <tbody>
            {table.map((row, i) => (
              <tr key={row.team}>
                <td style={{textAlign: 'left'}}><span className={clsx(classes.rank, {[classes.qualified]: places[i] == 1 && row.points > 0})}>{places[i]}</span></td>
                <td>{row.name}</td>
                {row.places.map((p, j) => <td key={j}><span className={classes.placeChip}>{p != null ? p : '–'}</span></td>)}
                <td className={classes.points}>{row.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface Props {
  tournament: ITournament;
  matches: IMatchView[];
  rounds: IRoundView[];
  /** open a match's detail (organizers also get its management tools there) */
  onMatchClick?: (match: IMatchView) => void;
  /** organizers: set a round's start time (D28) */
  onRoundTimeClick?: (target: RoundTimeTarget) => void;
}

/** The bracket, the groups, or the cup, depending on the tournament's stage. Shared by the app and the website. */
export const TournamentBracket: React.FC<Props> = (props) => (
  <MatchClick.Provider value={props.onMatchClick || null}>
    <RoundClick.Provider value={props.onRoundTimeClick || null}><BracketBody {...props} /></RoundClick.Provider>
  </MatchClick.Provider>
);

const BracketBody: React.FC<Props> = ({tournament: t, matches, rounds, onMatchClick}) => {
  const classes = useStyles();
  const groupMatches = matches.filter((m) => m.stage == MatchStage.Group);
  const playoff = matches.filter((m) => m.stage == MatchStage.Playoff);
  const winners = matches.filter((m) => m.stage == MatchStage.Winners);
  const losers = matches.filter((m) => m.stage == MatchStage.Losers);
  const finals = matches.filter((m) => m.stage == MatchStage.GrandFinal || m.stage == MatchStage.GrandFinalReset);
  const [view, setView] = useState<'groups' | 'playoff'>(playoff.length > 0 ? 'playoff' : 'groups');

  if(t.stage == TournamentStage.Cup)
    return rounds.length > 0 ? <Cup rounds={rounds} /> : <div className={classes.empty}>The cup's rounds will show here.</div>;
  // a shuffle (D30): standings and rounds
  if(t.stage == TournamentStage.Shuffle)
    return <ShuffleBoard tournament={t} matches={matches} onMatchClick={onMatchClick} me={matches.find((m) => m.me)?.me || null} />;
  if(matches.length == 0)
    return <div className={classes.empty}>The bracket will show here once the tournament starts.</div>;

  if(groupMatches.length > 0){
    return (
      <div>
        {playoff.length > 0 &&
          <div style={{marginBottom: 14}}>
            <Segmented value={view} onChange={setView} options={[{value: 'groups' as 'groups', label: 'Groups'}, {value: 'playoff' as 'playoff', label: 'Playoff'}]} />
          </div>
        }
        {view == 'playoff' && playoff.length > 0
          ? <Columns matches={playoff} title={(r) => eliminationRoundName(r, countRounds(playoff, MatchStage.Playoff))} />
          : <Groups matches={groupMatches} qualifiers={t.playoffQualifiers} />}
      </div>
    );
  }

  const winnersRounds = countRounds(winners, MatchStage.Winners);
  const double = losers.length > 0;
  return (
    <div>
      {double && <div className={classes.sectionTitle} style={{marginTop: 0}}>Winners bracket</div>}
      <Columns matches={winners} title={(r) => double ? (r == winnersRounds ? 'Winners final' : `Round ${r}`) : eliminationRoundName(r, winnersRounds)} />
      {double &&
        <React.Fragment>
          <div className={classes.sectionTitle}>Losers bracket</div>
          <Columns matches={losers} title={(r, total) => r == total ? 'Losers final' : `Round ${r}`} />
          <div className={classes.sectionTitle}>Grand final</div>
          <div className={classes.bracket}>
            {finals.map((m) => (
              <div key={String(m._id)}>
                {m.stage == MatchStage.GrandFinalReset
                  ? <div className={classes.columnTitle}>Reset (if needed)</div>
                  : <RoundTitle label="Grand final" stage={MatchStage.GrandFinal} round={1} total={1} matches={[m]} />}
                <MatchCard match={m} />
              </div>
            ))}
          </div>
        </React.Fragment>
      }
    </div>
  );
}

export const formatClear = formatMinutes;
